import {IHttpExceptionFactory} from '@src/@interfaces/services/Http/IHttpExceptionFactory';
import RequestAbortedHttpException from '@src/exceptions/RequestAbortedHttpException';
import ServerErrorHttpException from '@src/exceptions/ServerErrorHttpException';
import ValidationHttpException from '@src/exceptions/ValidationHttpException';
import NotFoundHttpException from '@src/exceptions/NotFoundHttpException';
import AccessDeniedHttpException from '@src/exceptions/AccessDeniedHttpException';
import UnauthorizedHttpException from '@src/exceptions/UnauthorizedHttpException';
import BadRequestHttpException from '@src/exceptions/BadRequestHttpException';
import UnexpectedResponseHttpException from '@src/exceptions/UnexpectedResponseHttpException';
import HttpException from '@src/exceptions/HttpException';
import DumpResponseHttpException from '@src/exceptions/DumpResponseHttpException';
import {UnsafeHTML} from '@src/@types/UnsafeHTML';
import ServerSQLErrorHttpException from '@src/exceptions/ServerSQLErrorHttpException';
import NotificationException from '@src/exceptions/NotificationException';
import CSRFTokenMismatchHttpException from '@src/exceptions/CSRFTokenMismatchHttpException';
import TooManyRequestsHttpException from '@src/exceptions/TooManyRequestsHttpException';

export default class HttpExceptionFactory implements IHttpExceptionFactory {
    createException(response: Response, textResponse?: string, data?: any, error?: Error): Error | HttpException {
        const errorMessage = error?.message || '';
        if (response.statusText === 'abort' || errorMessage.includes('user aborted a request')) {
            return new RequestAbortedHttpException(response);
        }

        if (data?.exception?.endsWith('NotificationAsResponseException')) {
            return new NotificationException(data.message ?? errorMessage);
        }

        if (this.isDumpResponse(response, textResponse)) {
            return new DumpResponseHttpException(response, textResponse as UnsafeHTML);
        }

        if (response.status === 500) {
            if (data?.message && (/^SQLSTATE\[.+]:\s/gim).test(data?.message)) {
                return new ServerSQLErrorHttpException(response, data);
            }

            return new ServerErrorHttpException(response, data);
        }

        if (response.status === 422) {
            return new ValidationHttpException(
                response,
                data?.errors ?? {},
                data?.message ?? response.statusText
            );
        }

        const httpException = {
            429: TooManyRequestsHttpException,
            419: CSRFTokenMismatchHttpException,
            404: NotFoundHttpException,
            403: AccessDeniedHttpException,
            401: UnauthorizedHttpException,
            400: BadRequestHttpException,
        }[response.status];

        if (httpException) {
            return new httpException(response, data?.message);
        }

        if (data != null && !Object.keys(data).length) {
            return new UnexpectedResponseHttpException(response);
        }

        return error ?? new HttpException(response, response.statusText);
    }

    protected isDumpResponse(response: Response, textResponse?: string): boolean {
        return response.status === 500 && textResponse?.includes('Sfdump = window.Sfdump') === true;
    }
}
